import CustomerDeliveryHistory from '../../components/CustomerDeliveryHistory';
import { customerHomePath } from '../../lib/customerHome';
import { deliveryAddressText, deliveryAddressLabel } from '../../lib/deliveryAddressText';
import ProductCard from '../../components/ProductCard';
import VendorApplication from '../../components/VendorApplication';
import AccountSettings from '../../components/AccountSettings';
import VerificationStatus from '../../components/VerificationStatus';
import CustomerNotifications from '../../components/CustomerNotifications';
import CustomerAddressDialog from '../../components/CustomerAddressDialog';
import useStoreCategories from '../../lib/useStoreCategories';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  User,
  MapPin,
  Bell,
  Heart,
  ShoppingBag,
  Settings,
  LogOut,
  Plus,
  ArrowLeft,
  Trash2,
  Pencil,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { get, post, put, del } from '../../lib/api';

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { categories: storeCategories } = useStoreCategories();

  const [profileData, setProfileData] = useState(null);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [favoritesError, setFavoritesError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [verificationMethod, setVerificationMethod] = useState('DOCUMENT');
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState(() => searchParams.get('section') === 'settings' ? 'settings' : null);

  useEffect(() => {
    if (!searchParams.has('section')) return;
    // Consume the entry link so returning through history opens Account.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('section');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);


  useEffect(() => {
    get('/customers/verification/settings').then(settings => setVerificationMethod(settings.verificationMethod || 'DOCUMENT')).catch(() => setError('Unable to load verification settings.'));
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id || user?.role !== 'CUSTOMER') {
        setLoadingProfile(false);
        return;
      }

      try {
        setLoadingProfile(true);
        setError('');

        const response = await get('/customers/me');

        if (response.success && response.customer) {
          setProfileData(response.customer);

        } else {
          setError(response.message || 'Failed to fetch profile.');
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);

        setError(
          err.response?.data?.message ||
            'An error occurred while fetching your profile.'
        );
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    if (activeSection !== 'favorites') return undefined;
    let current = true;
    let requestVersion = 0;
    const loadFavorites = async () => {
      const version = ++requestVersion;
      setLoadingFavorites(true);
      setFavoritesError('');
      try {
        const firstPage = await get('/catalog?favorites=true&allFavoriteCategories=true&productPage=1&storePage=1');
        const products = [...(firstPage.products || [])];
        for (let page = 2; page <= (firstPage.productPagination?.totalPages || 1); page += 1) {
          const response = await get(`/catalog?favorites=true&allFavoriteCategories=true&productPage=${page}&storePage=1`);
          products.push(...(response.products || []));
        }
        if (current && version === requestVersion) setFavoriteProducts(products);
      } catch (loadError) {
        if (current && version === requestVersion) setFavoritesError(loadError.response?.data?.message || 'Unable to load favorite products.');
      } finally {
        if (current && version === requestVersion) setLoadingFavorites(false);
      }
    };
    loadFavorites();
    window.addEventListener('favorites-updated', loadFavorites);
    return () => {
      current = false;
      window.removeEventListener('favorites-updated', loadFavorites);
    };
  }, [activeSection, user?.id]);

  const handleFavoriteToggle = async (productId) => {
    await del(`/customers/me/favorites/products/${encodeURIComponent(productId)}`);
    setFavoriteProducts((current) => current.filter((product) => String(product._id) !== String(productId)));
    window.dispatchEvent(new Event('favorites-updated'));
  };

  const handleAddFavoriteToCart = async (cartItem) => {
    const response = await post('/cart/items', cartItem);
    if (!response.success) throw new Error(response.message || 'Failed to add product to cart.');
    return response;
  };
  const favoriteGroups = favoriteProducts.reduce((groups, product) => {
    const category = product.store?.vendorCategory || 'other';
    (groups[category] ||= []).push(product);
    return groups;
  }, {});
  const favoriteCategoryLabel = (value) =>
    storeCategories.find((category) => category.value === value)?.label
      || (value === 'other' ? 'Other' : value.charAt(0).toUpperCase() + value.slice(1));

  const menuItems = [
    {
      key: 'orders',
      label: 'My Orders',
      icon: ShoppingBag,
      description: 'View and track your orders',
    },
    {
      key: 'addresses',
      label: 'Saved Addresses',
      icon: MapPin,
      description: 'Manage your delivery locations',
    },
    {
      key: 'favorites',
      label: 'Favorites',
      icon: Heart,
      description: 'View your preferred restaurants',
    },
    {
      key: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Read account and order updates',
    },
    {
      key: 'settings',
      label: 'Account Settings',
      icon: Settings,
      description: 'Update profile and security',
    },
  ];
const handleBackToProfile = () => {
  setActiveSection(null);
};
  const handleSaveAddress = async (address) => {
    setAddressError('');
    setAddressSaving(true);

    try {
      const response = editingAddressIndex === null
        ? await post('/customers/addresses', address)
        : await put('/customers/addresses/' + editingAddressIndex, address);
      if (response.success && response.customer) {
        setProfileData((current) => ({ ...current, addresses: response.customer.addresses }));
        setEditingAddressIndex(null);
        setAddressDialogOpen(false);
        return true;
      } else {
        setAddressError(
          response.message || 'Failed to save address.'
        );
      }
    } catch (err) {
      console.error('Failed to save address:', err);
      setAddressError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save address. Please try again.'
      );
    } finally {
      setAddressSaving(false);
    }
    return false;
  }

  const handleDeleteAddress = async (index) => {
    try {
      const response = await del(`/customers/addresses/${index}`);

      if (response.success && response.customer) {
        setProfileData((current) => ({ ...current, addresses: response.customer.addresses }));
        setEditingAddressIndex(null);
        setAddressError('');
      }
    } catch (err) {
      console.error('Failed to delete address:', err);

      setAddressError(
        err.response?.data?.message ||
          'Failed to delete address.'
      );
    }
  };

  if (loadingProfile) {
    return (
      <div className="py-8 text-center">
        Loading profile...
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center text-red-500">
        {error}
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="py-8 text-center">
        No profile data available.
      </div>
    );
  }

  return (
    <>
      <header className="mx-auto mb-3 flex max-w-4xl items-center gap-2">
        <Button type="button" variant="ghost" size="icon" aria-label={activeSection ? 'Back to profile' : 'Go back'} onClick={() => activeSection ? handleBackToProfile() : navigate(customerHomePath !== '/' ? customerHomePath : -1)}>
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Button>
        <h1 className="text-lg font-semibold">{menuItems.find((item) => item.key === activeSection)?.label || 'Account'}</h1>
      </header>
    <div className={`mx-auto ${activeSection ? 'max-w-4xl space-y-3' : 'max-w-4xl divide-y divide-gray-200 overflow-hidden rounded-lg border bg-white'}`}>
      {/* Profile Header */}
      {!activeSection && <div className="flex items-center gap-3 bg-white p-3">
        <div className="flex h-5 w-5 shrink-0 items-center justify-center">
          <User className="h-5 w-5 text-gray-500" />
        </div>

        <div className="min-w-0">
          <h1 className="break-words text-xl font-bold sm:text-2xl">
            {profileData.userId?.name || 'User'}
          </h1>

          <div className="mt-1 space-y-0.5 text-sm text-gray-500">
            {profileData.userId?.email && <p className="break-all">{profileData.userId.email.endsWith('.social-login.local') ? 'Add your email during verification' : profileData.userId.email}</p>}
            {profileData.userId?.phone && <p>{profileData.userId.phone}</p>}
          </div>

          <Button
            onClick={() => setActiveSection("settings")}
            variant="link"
            className="h-auto p-0 text-sm font-medium text-primary"
          >
            Edit Profile
          </Button>
        </div>
      </div>}

{!activeSection && <VerificationStatus customer={profileData} verificationMethod={verificationMethod} compactIcon className="bg-white p-3" />}
      {activeSection === 'notifications' && <div className="space-y-4"><CustomerNotifications compact /></div>}
{/* Profile Menu */}
{!activeSection && (
  <div className="divide-y divide-gray-200 bg-white">
    {menuItems.map((item) => {
      const Icon = item.icon;
      return (
        <button
          type="button"
          key={item.key}
          onClick={() => item.key === 'orders' ? navigate('/orders', { state: { fromProfile: true } }) : setActiveSection(item.key)}
          className="group flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-primary"
        >
          <Icon className="h-5 w-5 shrink-0 text-gray-500 transition-colors group-hover:text-primary" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="font-semibold">{item.label}</h3>
            <p className="text-sm text-gray-500">{item.description}</p>
          </div>
        </button>
      );
    })}
  </div>
)}
      {/* Account Settings */}
      {activeSection === 'settings' && (
        <div className="space-y-3">
          <AccountSettings onUpdated={async () => { try { const response = await get('/customers/me'); setProfileData(response.customer); } catch { setError('Details updated. Reload to refresh your profile.'); } }} />
  </div>
      )}

      {/* Saved Addresses */}
      {activeSection === 'addresses' && (
        <div className="space-y-3">


          <div className="space-y-4">
            {/* Saved Address List */}
            <Card className="rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold">
                Saved Addresses
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Add address"
                onClick={() => {
                  setEditingAddressIndex(null);
                  setAddressError('');
                  setAddressDialogOpen(true);
                }}
              >
                <Plus className="h-5 w-5" />
              </Button>
              </div>

              {profileData?.addresses?.length > 0 ? (
                <div className="space-y-3">
                  {profileData.addresses.map((addr, index) => (
                    <div
                      key={`${addr._id || index}-${addr.type}`}
                      className="py-3"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold">
                            {deliveryAddressLabel(addr)}
                          </p>

                          <p className="text-sm text-gray-500">
                            {deliveryAddressText(addr)}
                          </p>
                          <p className="mt-1 text-xs text-slate-500">{addr.branch?.name || addr.branchName || 'Outside platform areas'}</p>

                          <CustomerDeliveryHistory deliveries={addr.successfulDeliveries || []} />
                          {addr.deliveryLocked && <p className="mt-2 text-xs text-slate-500">Locked after successful delivery. Create a new address to make changes.</p>}
                          {addr.isDefault && (
                            <span className="mt-2 inline-flex rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                              Default
                            </span>
                          )}
                        </div>

                        {!addressSaving && (
                          <div className="flex shrink-0 items-center gap-1">
                            <Button disabled={addr.deliveryLocked} variant="ghost" size="icon" aria-label={`Edit ${deliveryAddressLabel(addr)} address`} title="Edit address" onClick={() => { setEditingAddressIndex(index); setAddressError(""); setAddressDialogOpen(true); }}>
                              <Pencil className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${deliveryAddressLabel(addr)} address`}
                            title="Delete address"
                            disabled={addr.deliveryLocked}
                            onClick={() =>
                              handleDeleteAddress(index)
                            }
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No saved addresses yet. Add one to make
                  checkout faster.
                </p>
              )}
            </Card>
          </div>
          {addressError && !addressDialogOpen && <p role="alert" className="text-sm text-red-600">{addressError}</p>}
          <CustomerAddressDialog
            open={addressDialogOpen}
            address={editingAddressIndex === null ? null : profileData?.addresses?.[editingAddressIndex]}
            saving={addressSaving}
            error={addressError}
            onClose={() => {
              setAddressDialogOpen(false);
              setEditingAddressIndex(null);
              setAddressError('');
            }}
            onSave={handleSaveAddress}
          />
        </div>
      )}

      {!activeSection && <VendorApplication />}
      {activeSection === 'favorites' && (
        <section className="space-y-4">

          {favoritesError && <p role="alert" className="text-red-700">{favoritesError}</p>}
          {loadingFavorites && <p role="status" className="text-slate-500">Loading favorite products...</p>}
          {!loadingFavorites && !favoritesError && favoriteProducts.length === 0 && (
            <p className="rounded-xl border bg-white p-6 text-slate-500">Your favorite products will appear here. Save products using the heart button while browsing.</p>
          )}
          {!favoritesError && Object.entries(favoriteGroups).sort(([a], [b]) =>
            favoriteCategoryLabel(a).localeCompare(favoriteCategoryLabel(b)),
          ).map(([category, products]) => (
            <section key={category} className="space-y-3">
              <h3 className="text-lg font-semibold">{favoriteCategoryLabel(category)}</h3>
              <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
                {products.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    vendor={product.store}
                    onAddToCart={handleAddFavoriteToCart}
                    canFavorite
                    isFavorite
                    onToggleFavorite={handleFavoriteToggle}
                  />
                ))}
              </div>
            </section>
          ))}
        </section>
      )}
      {/* Logout */}
      {!activeSection && <div className="border-t pt-8">
        <Button
          variant="ghost"
          className="gap-2 text-destructive hover:bg-destructive/5 hover:text-destructive"
          onClick={logout}
        >
          <LogOut className="h-5 w-5" />
          Log Out
        </Button>
      </div>}
    </div>
    </>
  );
};

export default Profile;
